/**
 * Business Card Detection Utilities
 * Uses computer vision techniques to automatically detect business card edges
 */

import { lineDetector } from './line-detection.js';
import { colorDetector } from './color-segmentation.js';

export class CardDetector {
  constructor() {
    this.cv = null;
    this.initialized = false;
  }

  /**
   * Initialize OpenCV.js (if available)
   * Falls back to simpler edge detection if OpenCV is not loaded
   */
  async initialize() {
    // Check if we're already initialized
    if (this.initialized) {
      return true;
    }

    // Wait for OpenCV to be ready (if available)
    if (window.waitForOpenCV) {
      const opencvAvailable = await new Promise(resolve => {
        window.waitForOpenCV(resolve, 3000);
      });
      
      if (opencvAvailable && typeof cv !== 'undefined' && cv && cv.Mat) {
        this.cv = cv;
        this.initialized = true;
        console.log('OpenCV.js initialized for card detection');
        return true;
      }
    }
    
    // Check directly if OpenCV is available
    if (typeof cv !== 'undefined' && cv && cv.Mat) {
      this.cv = cv;
      this.initialized = true;
      console.log('OpenCV.js initialized for card detection');
    } else {
      console.log('OpenCV.js not available, using enhanced fallback detection');
      this.initialized = true;
    }
    return this.initialized;
  }

  /**
   * Detect business card corners in an image
   * @param {HTMLImageElement} image - The source image
   * @param {Object} options - Detection options
   * @returns {Array} Array of four corner points [{x, y}, ...]
   */
  async detectCardCorners(image, options = {}) {
    const {
      minArea = 0.1, // Minimum area as fraction of image
      maxArea = 0.9, // Maximum area as fraction of image
      aspectRatioTolerance = 0.3, // Tolerance for business card aspect ratio
      targetAspectRatio = 1.75 // Standard business card aspect ratio (3.5" x 2")
    } = options;

    // Try line-based detection first (most reliable)
    try {
      console.log('Attempting line-based detection');
      const corners = await lineDetector.detectCard(image);
      if (corners && corners.length === 4) {
        console.log('Line-based detection succeeded');
        return corners;
      }
    } catch (error) {
      console.warn('Line-based detection failed:', error);
    }

    // Try color segmentation detection
    try {
      console.log('Attempting color segmentation detection');
      const corners = await colorDetector.detectCard(image);
      if (corners && corners.length === 4) {
        console.log('Color segmentation detection succeeded');
        return corners;
      }
    } catch (error) {
      console.warn('Color segmentation detection failed:', error);
    }

    // Fall back to OpenCV if available
    if (this.cv && this.initialized && typeof cv !== 'undefined' && cv.Mat) {
      try {
        console.log('Attempting OpenCV detection');
        return this.detectWithOpenCV(image, options);
      } catch (error) {
        console.warn('OpenCV detection failed, falling back:', error);
        return this.detectWithFallback(image, options);
      }
    } else {
      return this.detectWithFallback(image, options);
    }
  }

  /**
   * OpenCV-based detection (more accurate)
   */
  detectWithOpenCV(image, options) {
    console.log('Starting OpenCV detection');
    try {
      // Create canvas and get image data
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = image.width;
      canvas.height = image.height;
      ctx.drawImage(image, 0, 0);

      // Convert to OpenCV Mat
      const src = this.cv.imread(canvas);
      console.log('Image loaded into OpenCV Mat:', src.rows, 'x', src.cols);
      const gray = new this.cv.Mat();
      const blur = new this.cv.Mat();
      const edges = new this.cv.Mat();
      const hierarchy = new this.cv.Mat();
      const contours = new this.cv.MatVector();
      const adaptiveThresh = new this.cv.Mat();

      // Convert to grayscale
      this.cv.cvtColor(src, gray, this.cv.COLOR_RGBA2GRAY);

      // Apply Gaussian blur to reduce noise (increased kernel size for noisier images)
      this.cv.GaussianBlur(gray, blur, new this.cv.Size(7, 7), 0);

      // Apply adaptive threshold to handle varying lighting conditions
      this.cv.adaptiveThreshold(blur, adaptiveThresh, 255, 
        this.cv.ADAPTIVE_THRESH_GAUSSIAN_C, 
        this.cv.THRESH_BINARY, 11, 2);

      // Edge detection using Canny with lower thresholds to detect weaker edges
      // Use both the blur and adaptive threshold for better edge detection
      this.cv.Canny(blur, edges, 30, 100);
      
      // Combine edges from adaptive threshold
      const adaptiveEdges = new this.cv.Mat();
      this.cv.Canny(adaptiveThresh, adaptiveEdges, 50, 150);
      this.cv.bitwise_or(edges, adaptiveEdges, edges);
      adaptiveEdges.delete();

      // Add morphological closing to connect broken edges and fill small gaps
      const kernel = this.cv.getStructuringElement(this.cv.MORPH_ELLIPSE, new this.cv.Size(3, 3));
      this.cv.morphologyEx(edges, edges, this.cv.MORPH_CLOSE, kernel);
      kernel.delete();

      // Find contours
      this.cv.findContours(edges, contours, hierarchy, this.cv.RETR_EXTERNAL, this.cv.CHAIN_APPROX_SIMPLE);
      console.log('Found', contours.size(), 'contours');

      // Sort contours by area descending to prioritize the largest (likely the card)
      let sortedContours = [];
      for (let i = 0; i < contours.size(); i++) {
        const contour = contours.get(i);
        const area = this.cv.contourArea(contour);
        sortedContours.push({ contour, area });
      }
      sortedContours.sort((a, b) => b.area - a.area);
      
      if (sortedContours.length > 0) {
        console.log('Largest contour area:', sortedContours[0].area, 'pixels');
      }

      let bestContourPoints = null;
      let bestScore = 0;

      // Check top 5 largest contours
      for (let i = 0; i < Math.min(5, sortedContours.length); i++) {
        const { contour, area } = sortedContours[i];
        const imageArea = src.rows * src.cols;
        const areaRatio = area / imageArea;

        // Skip if area is too small or too large
        if (areaRatio < options.minArea || areaRatio > options.maxArea) {
          continue;
        }

        // Approximate contour to polygon with relaxed epsilon for better quad approximation
        const epsilon = 0.05 * this.cv.arcLength(contour, true);
        const approx = new this.cv.Mat();
        this.cv.approxPolyDP(contour, approx, epsilon, true);

        let points;
        
        // If we have 4 or more points, use them (or extract the 4 most relevant)
        if (approx.rows >= 4) {
          points = [];
          // If we have exactly 4 points, use them
          if (approx.rows === 4) {
            for (let j = 0; j < 4; j++) {
              const point = approx.data32S.slice(j * 2, j * 2 + 2);
              points.push({ x: point[0], y: point[1] });
            }
          } else {
            // If more than 4 points, use minAreaRect or boundingRect for a fitted quad
            try {
              // Try minAreaRect first (better for rotated cards)
              if (this.cv.minAreaRect) {
                const rect = this.cv.minAreaRect(contour);
                
                // Check if RotatedRect.points is available
                if (this.cv.RotatedRect && this.cv.RotatedRect.points) {
                  const vertices = this.cv.RotatedRect.points(rect);
                  points = [
                    { x: vertices[0].x, y: vertices[0].y },
                    { x: vertices[1].x, y: vertices[1].y },
                    { x: vertices[2].x, y: vertices[2].y },
                    { x: vertices[3].x, y: vertices[3].y }
                  ];
                } else {
                  // Compute rotated rect points manually
                  const center = rect.center;
                  const size = rect.size;
                  const angle = rect.angle * Math.PI / 180;
                  
                  const b = Math.cos(angle) * 0.5;
                  const a = Math.sin(angle) * 0.5;
                  
                  points = [
                    { x: center.x - a * size.height - b * size.width,
                      y: center.y + b * size.height - a * size.width },
                    { x: center.x + a * size.height - b * size.width,
                      y: center.y - b * size.height - a * size.width },
                    { x: center.x + a * size.height + b * size.width,
                      y: center.y - b * size.height + a * size.width },
                    { x: center.x - a * size.height + b * size.width,
                      y: center.y + b * size.height + a * size.width }
                  ];
                }
              } else {
                // Fallback to boundingRect if minAreaRect not available
                const rect = this.cv.boundingRect(contour);
                points = [
                  { x: rect.x, y: rect.y },
                  { x: rect.x + rect.width, y: rect.y },
                  { x: rect.x + rect.width, y: rect.y + rect.height },
                  { x: rect.x, y: rect.y + rect.height }
                ];
              }
            } catch (e) {
              console.warn('Failed to compute rect from contour, using boundingRect:', e);
              // Ultimate fallback to boundingRect
              const rect = this.cv.boundingRect(contour);
              points = [
                { x: rect.x, y: rect.y },
                { x: rect.x + rect.width, y: rect.y },
                { x: rect.x + rect.width, y: rect.y + rect.height },
                { x: rect.x, y: rect.y + rect.height }
              ];
            }
          }
        } else if (approx.rows === 3) {
          // For triangular approximations, use boundingRect
          const rect = this.cv.boundingRect(contour);
          points = [
            { x: rect.x, y: rect.y },
            { x: rect.x + rect.width, y: rect.y },
            { x: rect.x + rect.width, y: rect.y + rect.height },
            { x: rect.x, y: rect.y + rect.height }
          ];
        } else {
          // Fallback to minAreaRect or boundingRect for rounded/imperfect contours
          try {
            if (this.cv.minAreaRect) {
              const rect = this.cv.minAreaRect(contour);
              
              if (this.cv.RotatedRect && this.cv.RotatedRect.points) {
                const vertices = this.cv.RotatedRect.points(rect);
                points = [
                  { x: vertices[0].x, y: vertices[0].y },
                  { x: vertices[1].x, y: vertices[1].y },
                  { x: vertices[2].x, y: vertices[2].y },
                  { x: vertices[3].x, y: vertices[3].y }
                ];
              } else {
                // Compute rotated rect points manually
                const center = rect.center;
                const size = rect.size;
                const angle = rect.angle * Math.PI / 180;
                
                const b = Math.cos(angle) * 0.5;
                const a = Math.sin(angle) * 0.5;
                
                points = [
                  { x: center.x - a * size.height - b * size.width,
                    y: center.y + b * size.height - a * size.width },
                  { x: center.x + a * size.height - b * size.width,
                    y: center.y - b * size.height - a * size.width },
                  { x: center.x + a * size.height + b * size.width,
                    y: center.y - b * size.height + a * size.width },
                  { x: center.x - a * size.height + b * size.width,
                    y: center.y + b * size.height + a * size.width }
                ];
              }
            } else {
              // Use boundingRect if minAreaRect not available
              const rect = this.cv.boundingRect(contour);
              points = [
                { x: rect.x, y: rect.y },
                { x: rect.x + rect.width, y: rect.y },
                { x: rect.x + rect.width, y: rect.y + rect.height },
                { x: rect.x, y: rect.y + rect.height }
              ];
            }
          } catch (e) {
            console.warn('Failed to fit rectangle to contour:', e);
            // Ultimate fallback
            const rect = this.cv.boundingRect(contour);
            points = [
              { x: rect.x, y: rect.y },
              { x: rect.x + rect.width, y: rect.y },
              { x: rect.x + rect.width, y: rect.y + rect.height },
              { x: rect.x, y: rect.y + rect.height }
            ];
          }
        }

        approx.delete();

        // Compute aspect ratio from points
        const minX = Math.min(...points.map(p => p.x));
        const maxX = Math.max(...points.map(p => p.x));
        const minY = Math.min(...points.map(p => p.y));
        const maxY = Math.max(...points.map(p => p.y));
        const aspectRatio = (maxX - minX) / (maxY - minY);
        const aspectDiff = Math.abs(aspectRatio - options.targetAspectRatio);

        // Score based on area and aspect ratio
        const score = areaRatio * (1 - aspectDiff / options.targetAspectRatio);

        if (score > bestScore) {
          bestScore = score;
          bestContourPoints = points;
        }
      }

      // Clean up
      src.delete();
      gray.delete();
      blur.delete();
      edges.delete();
      hierarchy.delete();
      contours.delete();
      adaptiveThresh.delete();

      if (bestContourPoints) {
        console.log('OpenCV detected corners:', bestContourPoints);
        // Order points: top-left, top-right, bottom-right, bottom-left
        return this.orderCornerPoints(bestContourPoints);
      }

      console.log('OpenCV detection found no suitable contours');
      return null;
    } catch (error) {
      console.error('OpenCV detection failed:', error.message || error);
      console.log('Falling back to non-OpenCV detection');
      return this.detectWithFallback(image, options);
    }
  }

  /**
   * Fallback detection using canvas-based edge detection
   */
  detectWithFallback(image, options) {
    console.log('Using enhanced fallback detection for business card');
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);

    // Try multiple detection strategies
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let detectedCorners = null;
    
    // Strategy 1: Use the new bright region detection
    detectedCorners = this.detectLargestBrightRegion(imageData.data, canvas.width, canvas.height, options);
    
    if (!detectedCorners || detectedCorners.length !== 4) {
      // Strategy 2: Contrast-based detection
      detectedCorners = this.detectByContrast(ctx, canvas.width, canvas.height, options);
    }
    
    if (!detectedCorners || detectedCorners.length !== 4) {
      // Strategy 3: Edge-based detection with improved algorithm
      const edges = this.detectEdgesSimple(imageData);
      const corners = this.findCornersInEdges(edges, canvas.width, canvas.height);
      
      if (corners.length >= 4) {
        detectedCorners = this.selectBestQuadrilateral(corners, options);
      }
    }
    
    if (detectedCorners && detectedCorners.length === 4) {
      console.log('Successfully detected corners in fallback');
      return this.orderCornerPoints(detectedCorners);
    }

    // If no good corners found, return smart default rectangle
    return this.getSmartDefaultCorners(canvas, ctx, options);
  }

  /**
   * Simple edge detection using Sobel-like operator
   */
  detectEdgesSimple(imageData) {
    const { data, width, height } = imageData;
    const edges = new Uint8ClampedArray(width * height);

    // Convert to grayscale and detect edges
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        
        // Convert to grayscale
        const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        
        // Simple edge detection (Sobel approximation)
        const gx = (
          -1 * this.getGrayValue(data, x - 1, y - 1, width) +
          1 * this.getGrayValue(data, x + 1, y - 1, width) +
          -2 * this.getGrayValue(data, x - 1, y, width) +
          2 * this.getGrayValue(data, x + 1, y, width) +
          -1 * this.getGrayValue(data, x - 1, y + 1, width) +
          1 * this.getGrayValue(data, x + 1, y + 1, width)
        );

        const gy = (
          -1 * this.getGrayValue(data, x - 1, y - 1, width) +
          -2 * this.getGrayValue(data, x, y - 1, width) +
          -1 * this.getGrayValue(data, x + 1, y - 1, width) +
          1 * this.getGrayValue(data, x - 1, y + 1, width) +
          2 * this.getGrayValue(data, x, y + 1, width) +
          1 * this.getGrayValue(data, x + 1, y + 1, width)
        );

        const magnitude = Math.sqrt(gx * gx + gy * gy);
        edges[y * width + x] = magnitude > 50 ? 255 : 0;
      }
    }

    return edges;
  }

  getGrayValue(data, x, y, width) {
    const idx = (y * width + x) * 4;
    return (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
  }

  /**
   * Find corner candidates in edge image
   */
  findCornersInEdges(edges, width, height) {
    const corners = [];
    const blockSize = Math.min(width, height) / 20; // Adaptive block size

    // Harris corner detection approximation
    for (let y = blockSize; y < height - blockSize; y += blockSize / 2) {
      for (let x = blockSize; x < width - blockSize; x += blockSize / 2) {
        let cornerStrength = 0;
        let edgeCount = 0;

        // Check for edges in surrounding area
        for (let dy = -blockSize/2; dy <= blockSize/2; dy++) {
          for (let dx = -blockSize/2; dx <= blockSize/2; dx++) {
            const px = Math.floor(x + dx);
            const py = Math.floor(y + dy);
            if (px >= 0 && px < width && py >= 0 && py < height) {
              if (edges[py * width + px] > 0) {
                edgeCount++;
              }
            }
          }
        }

        // Calculate corner strength based on edge density
        const totalPixels = blockSize * blockSize;
        const edgeRatio = edgeCount / totalPixels;
        
        // Good corners have moderate edge density (not too sparse, not too dense)
        if (edgeRatio > 0.1 && edgeRatio < 0.8) {
          cornerStrength = edgeRatio * (1 - Math.abs(edgeRatio - 0.3));
          corners.push({ x, y, strength: cornerStrength });
        }
      }
    }

    // Sort by strength and return top candidates
    return corners.sort((a, b) => b.strength - a.strength).slice(0, 12);
  }

  /**
   * Select the best 4 corners that form a good quadrilateral
   */
  selectBestQuadrilateral(corners, options) {
    if (corners.length < 4) {
      return corners.slice(0, 4);
    }

    let bestScore = 0;
    let bestQuad = corners.slice(0, 4);

    // Try different combinations of 4 corners
    for (let i = 0; i < Math.min(corners.length - 3, 8); i++) {
      for (let j = i + 1; j < Math.min(corners.length - 2, 9); j++) {
        for (let k = j + 1; k < Math.min(corners.length - 1, 10); k++) {
          for (let l = k + 1; l < Math.min(corners.length, 11); l++) {
            const quad = [corners[i], corners[j], corners[k], corners[l]];
            const score = this.scoreQuadrilateral(quad, options);
            
            if (score > bestScore) {
              bestScore = score;
              bestQuad = quad;
            }
          }
        }
      }
    }

    return bestQuad;
  }

  /**
   * Score a quadrilateral based on how well it resembles a business card
   */
  scoreQuadrilateral(quad, options) {
    // Calculate bounding rectangle
    const minX = Math.min(...quad.map(p => p.x));
    const maxX = Math.max(...quad.map(p => p.x));
    const minY = Math.min(...quad.map(p => p.y));
    const maxY = Math.max(...quad.map(p => p.y));

    const width = maxX - minX;
    const height = maxY - minY;
    const aspectRatio = width / height;

    // Score based on aspect ratio similarity to business card
    const aspectScore = 1 - Math.abs(aspectRatio - options.targetAspectRatio) / options.targetAspectRatio;

    // Score based on how well the corners are distributed
    const distributionScore = this.calculateDistributionScore(quad, width, height);

    return aspectScore * 0.6 + distributionScore * 0.4;
  }

  /**
   * Calculate how well distributed the corners are
   */
  calculateDistributionScore(quad, width, height) {
    // Check if corners are roughly at the expected positions
    const center = { x: width / 2, y: height / 2 };
    let score = 0;

    quad.forEach(corner => {
      // Distance from center (normalized)
      const distFromCenter = Math.sqrt(
        Math.pow(corner.x - center.x, 2) + Math.pow(corner.y - center.y, 2)
      ) / Math.sqrt(width * width + height * height);

      // Good corners should be at a reasonable distance from center
      if (distFromCenter > 0.2 && distFromCenter < 0.7) {
        score += 1;
      }
    });

    return score / quad.length;
  }

  /**
   * Order corner points in consistent order: top-left, top-right, bottom-right, bottom-left
   */
  orderCornerPoints(points) {
    if (points.length !== 4) {
      return points;
    }

    // Sort by y coordinate to get top and bottom pairs
    const sorted = [...points].sort((a, b) => a.y - b.y);
    
    // Top two points
    const topPoints = sorted.slice(0, 2).sort((a, b) => a.x - b.x);
    // Bottom two points
    const bottomPoints = sorted.slice(2, 4).sort((a, b) => a.x - b.x);

    return [
      topPoints[0],    // top-left
      topPoints[1],    // top-right
      bottomPoints[1], // bottom-right
      bottomPoints[0]  // bottom-left
    ];
  }

  /**
   * Detect card by analyzing contrast regions
   */
  detectByContrast(ctx, width, height, options) {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    
    console.log('Attempting contrast-based detection');
    
    // Find high contrast regions (likely card edges)
    const contrastMap = new Uint8Array(width * height);
    let highContrastCount = 0;
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        
        // Check contrast with neighbors
        let maxContrast = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nIdx = ((y + dy) * width + (x + dx)) * 4;
            const nBrightness = (data[nIdx] + data[nIdx + 1] + data[nIdx + 2]) / 3;
            maxContrast = Math.max(maxContrast, Math.abs(brightness - nBrightness));
          }
        }
        
        if (maxContrast > 30) {
          contrastMap[y * width + x] = 255;
          highContrastCount++;
        } else {
          contrastMap[y * width + x] = 0;
        }
      }
    }
    
    console.log('Found', highContrastCount, 'high contrast pixels');
    
    // Find the largest rectangular region
    return this.findLargestRectangle(contrastMap, width, height, options);
  }
  
  /**
   * Find the largest rectangular region in the contrast map
   */
  findLargestRectangle(contrastMap, width, height, options) {
    // Look for continuous edge lines that might form a rectangle
    let edges = [];
    
    // Scan for horizontal edges
    for (let y = 10; y < height - 10; y++) {
      let edgeStrength = 0;
      let startX = -1, endX = -1;
      
      for (let x = 0; x < width; x++) {
        if (contrastMap[y * width + x] > 0) {
          if (startX === -1) startX = x;
          endX = x;
          edgeStrength++;
        }
      }
      
      // If we found a strong horizontal edge
      if (edgeStrength > width * 0.3 && endX - startX > width * 0.4) {
        edges.push({ type: 'horizontal', y, startX, endX, strength: edgeStrength });
      }
    }
    
    // Scan for vertical edges
    for (let x = 10; x < width - 10; x++) {
      let edgeStrength = 0;
      let startY = -1, endY = -1;
      
      for (let y = 0; y < height; y++) {
        if (contrastMap[y * width + x] > 0) {
          if (startY === -1) startY = y;
          endY = y;
          edgeStrength++;
        }
      }
      
      // If we found a strong vertical edge
      if (edgeStrength > height * 0.3 && endY - startY > height * 0.4) {
        edges.push({ type: 'vertical', x, startY, endY, strength: edgeStrength });
      }
    }
    
    console.log('Found', edges.length, 'potential edges');
    
    // Try to find a rectangle from the edges
    if (edges.length >= 4) {
      const horizontalEdges = edges.filter(e => e.type === 'horizontal').sort((a, b) => a.y - b.y);
      const verticalEdges = edges.filter(e => e.type === 'vertical').sort((a, b) => a.x - b.x);
      
      if (horizontalEdges.length >= 2 && verticalEdges.length >= 2) {
        // Use the outermost edges
        const topEdge = horizontalEdges[0];
        const bottomEdge = horizontalEdges[horizontalEdges.length - 1];
        const leftEdge = verticalEdges[0];
        const rightEdge = verticalEdges[verticalEdges.length - 1];
        
        return [
          { x: leftEdge.x, y: topEdge.y },
          { x: rightEdge.x, y: topEdge.y },
          { x: rightEdge.x, y: bottomEdge.y },
          { x: leftEdge.x, y: bottomEdge.y }
        ];
      }
    }
    
    return null;
  }
  
  /**
   * Get smart default corners based on image analysis
   */
  getSmartDefaultCorners(canvas, ctx, options) {
    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    
    console.log('Analyzing image for smart corners:', width, 'x', height);
    
    // Try multiple detection strategies
    let detectedCorners = null;
    
    // Strategy 1: Detect largest connected bright region
    detectedCorners = this.detectLargestBrightRegion(data, width, height, options);
    
    if (!detectedCorners || detectedCorners.length !== 4) {
      // Strategy 2: Use adaptive thresholding
      detectedCorners = this.detectWithAdaptiveThreshold(data, width, height, options);
    }
    
    if (!detectedCorners || detectedCorners.length !== 4) {
      // Strategy 3: Edge-based detection
      detectedCorners = this.detectByEdgeDensity(data, width, height, options);
    }
    
    if (detectedCorners && detectedCorners.length === 4) {
      console.log('Successfully detected card corners');
      return detectedCorners;
    }
    
    console.log('All detection strategies failed, using centered rectangle');
    // Fallback to centered rectangle with standard aspect ratio
    return this.getDefaultCardCorners(width, height, options);
  }
  
  /**
   * Detect the largest connected bright region (likely business card)
   */
  detectLargestBrightRegion(data, width, height, options) {
    console.log('Strategy 1: Grid-based bright region detection');
    
    // Use grid-based analysis for more robust detection
    const gridSize = 16; // Divide image into 16x16 grid
    const cellWidth = Math.floor(width / gridSize);
    const cellHeight = Math.floor(height / gridSize);
    
    // Analyze each grid cell
    const grid = [];
    for (let gy = 0; gy < gridSize; gy++) {
      grid[gy] = [];
      for (let gx = 0; gx < gridSize; gx++) {
        const startX = gx * cellWidth;
        const startY = gy * cellHeight;
        const endX = Math.min((gx + 1) * cellWidth, width);
        const endY = Math.min((gy + 1) * cellHeight, height);
        
        let cellBrightness = 0;
        let cellPixelCount = 0;
        
        for (let y = startY; y < endY; y++) {
          for (let x = startX; x < endX; x++) {
            const idx = (y * width + x) * 4;
            cellBrightness += (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
            cellPixelCount++;
          }
        }
        
        grid[gy][gx] = {
          x: gx,
          y: gy,
          avgBrightness: cellBrightness / cellPixelCount,
          centerX: startX + cellWidth / 2,
          centerY: startY + cellHeight / 2
        };
      }
    }
    
    // Find the threshold using Otsu's method on grid cells
    const allBrightness = [];
    for (let gy = 0; gy < gridSize; gy++) {
      for (let gx = 0; gx < gridSize; gx++) {
        allBrightness.push(grid[gy][gx].avgBrightness);
      }
    }
    allBrightness.sort((a, b) => a - b);
    
    const median = allBrightness[Math.floor(allBrightness.length / 2)];
    const q75 = allBrightness[Math.floor(allBrightness.length * 0.75)];
    const threshold = Math.min(median + (q75 - median) * 0.7, 180);
    
    console.log('Grid analysis - Median:', median.toFixed(1), 'Q75:', q75.toFixed(1), 'Threshold:', threshold.toFixed(1));
    
    // Find bright cells
    const brightCells = [];
    for (let gy = 0; gy < gridSize; gy++) {
      for (let gx = 0; gx < gridSize; gx++) {
        if (grid[gy][gx].avgBrightness > threshold) {
          brightCells.push(grid[gy][gx]);
        }
      }
    }
    
    console.log('Found', brightCells.length, 'bright cells out of', gridSize * gridSize);
    
    if (brightCells.length < 4 || brightCells.length > gridSize * gridSize * 0.8) {
      console.log('Bright cell count out of expected range');
      return null;
    }
    
    // Find connected bright region
    let minGX = gridSize, maxGX = 0;
    let minGY = gridSize, maxGY = 0;
    
    for (const cell of brightCells) {
      minGX = Math.min(minGX, cell.x);
      maxGX = Math.max(maxGX, cell.x);
      minGY = Math.min(minGY, cell.y);
      maxGY = Math.max(maxGY, cell.y);
    }
    
    // Convert grid coordinates to pixel coordinates
    let minX = minGX * cellWidth;
    let maxX = Math.min((maxGX + 1) * cellWidth, width - 1);
    let minY = minGY * cellHeight;
    let maxY = Math.min((maxGY + 1) * cellHeight, height - 1);
    
    // Refine bounds by scanning actual pixels at edges
    const scanDepth = Math.min(cellWidth, cellHeight);
    
    // Refine left edge
    let foundEdge = false;
    for (let x = minX; x < minX + scanDepth && x < width; x++) {
      let columnBrightCount = 0;
      for (let y = minY; y < maxY && y < height; y++) {
        const idx = (y * width + x) * 4;
        const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        if (brightness > threshold) columnBrightCount++;
      }
      if (columnBrightCount > (maxY - minY) * 0.3) {
        minX = x;
        foundEdge = true;
        break;
      }
    }
    
    // Refine right edge
    foundEdge = false;
    for (let x = maxX; x > maxX - scanDepth && x >= 0; x--) {
      let columnBrightCount = 0;
      for (let y = minY; y < maxY && y < height; y++) {
        const idx = (y * width + x) * 4;
        const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        if (brightness > threshold) columnBrightCount++;
      }
      if (columnBrightCount > (maxY - minY) * 0.3) {
        maxX = x;
        foundEdge = true;
        break;
      }
    }
    
    // Refine top edge
    foundEdge = false;
    for (let y = minY; y < minY + scanDepth && y < height; y++) {
      let rowBrightCount = 0;
      for (let x = minX; x < maxX && x < width; x++) {
        const idx = (y * width + x) * 4;
        const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        if (brightness > threshold) rowBrightCount++;
      }
      if (rowBrightCount > (maxX - minX) * 0.3) {
        minY = y;
        foundEdge = true;
        break;
      }
    }
    
    // Refine bottom edge
    foundEdge = false;
    for (let y = maxY; y > maxY - scanDepth && y >= 0; y--) {
      let rowBrightCount = 0;
      for (let x = minX; x < maxX && x < width; x++) {
        const idx = (y * width + x) * 4;
        const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        if (brightness > threshold) rowBrightCount++;
      }
      if (rowBrightCount > (maxX - minX) * 0.3) {
        maxY = y;
        foundEdge = true;
        break;
      }
    }
    
    console.log('Refined bounds:', minX, minY, 'to', maxX, maxY);
    
    const regionWidth = maxX - minX;
    const regionHeight = maxY - minY;
    const aspectRatio = regionWidth / regionHeight;
    
    console.log('Region size:', regionWidth, 'x', regionHeight, 'Aspect ratio:', aspectRatio.toFixed(2));
    
    // Check if this looks like a business card
    const isLandscape = aspectRatio > 1.3 && aspectRatio < 2.3;
    const isPortrait = aspectRatio > 0.43 && aspectRatio < 0.77;
    const isSizeReasonable = regionWidth > width * 0.15 && regionHeight > height * 0.15;
    
    if ((isLandscape || isPortrait) && isSizeReasonable) {
      // Add small padding
      const padding = Math.min(5, Math.min(width, height) * 0.005);
      const corners = [
        { x: Math.max(0, minX - padding), y: Math.max(0, minY - padding) },
        { x: Math.min(width - 1, maxX + padding), y: Math.max(0, minY - padding) },
        { x: Math.min(width - 1, maxX + padding), y: Math.min(height - 1, maxY + padding) },
        { x: Math.max(0, minX - padding), y: Math.min(height - 1, maxY + padding) }
      ];
      
      console.log('Successfully detected corners:', corners);
      return corners;
    } else {
      console.log('Region does not match business card proportions');
      return null;
    }
  }
  
  /**
   * Flood fill to find connected component
   */
  floodFill(binary, visited, startX, startY, width, height) {
    const stack = [{x: startX, y: startY}];
    const region = {
      minX: startX,
      maxX: startX,
      minY: startY,
      maxY: startY,
      pixels: 0
    };
    
    // Limit flood fill to prevent stack overflow
    const maxPixels = width * height * 0.9;
    
    while (stack.length > 0 && region.pixels < maxPixels) {
      const {x, y} = stack.pop();
      
      if (x < 0 || x >= width || y < 0 || y >= height) {
        continue;
      }
      
      const idx = y * width + x;
      if (visited[idx] === 1 || binary[idx] === 0) {
        continue;
      }
      
      visited[idx] = 1;
      region.pixels++;
      region.minX = Math.min(region.minX, x);
      region.maxX = Math.max(region.maxX, x);
      region.minY = Math.min(region.minY, y);
      region.maxY = Math.max(region.maxY, y);
      
      // Add neighbors to stack
      stack.push({x: x + 1, y});
      stack.push({x: x - 1, y});
      stack.push({x, y: y + 1});
      stack.push({x, y: y - 1});
    }
    
    return region;
  }
  
  /**
   * Detect using adaptive thresholding
   */
  detectWithAdaptiveThreshold(data, width, height, options) {
    console.log('Strategy 2: Adaptive threshold detection');
    
    // Divide image into blocks and find local thresholds
    const blockSize = 50;
    const blocks = [];
    
    for (let by = 0; by < height; by += blockSize) {
      for (let bx = 0; bx < width; bx += blockSize) {
        let blockSum = 0;
        let blockCount = 0;
        
        for (let y = by; y < Math.min(by + blockSize, height); y++) {
          for (let x = bx; x < Math.min(bx + blockSize, width); x++) {
            const idx = (y * width + x) * 4;
            blockSum += (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
            blockCount++;
          }
        }
        
        blocks.push({
          x: bx,
          y: by,
          avgBrightness: blockSum / blockCount
        });
      }
    }
    
    // Find the brightest connected region of blocks
    blocks.sort((a, b) => b.avgBrightness - a.avgBrightness);
    const brightBlocks = blocks.filter(b => b.avgBrightness > 150);
    
    if (brightBlocks.length > 0) {
      let minX = width, maxX = 0;
      let minY = height, maxY = 0;
      
      brightBlocks.forEach(block => {
        minX = Math.min(minX, block.x);
        maxX = Math.max(maxX, block.x + blockSize);
        minY = Math.min(minY, block.y);
        maxY = Math.max(maxY, block.y + blockSize);
      });
      
      const regionWidth = maxX - minX;
      const regionHeight = maxY - minY;
      const aspectRatio = regionWidth / regionHeight;
      
      if ((aspectRatio > 1.4 && aspectRatio < 2.2) || (aspectRatio > 0.45 && aspectRatio < 0.72)) {
        return [
          { x: minX, y: minY },
          { x: maxX, y: minY },
          { x: maxX, y: maxY },
          { x: minX, y: maxY }
        ];
      }
    }
    
    return null;
  }
  
  /**
   * Detect by finding region with highest edge density
   */
  detectByEdgeDensity(data, width, height, options) {
    console.log('Strategy 3: Edge density detection');
    
    // Compute edge map
    const edges = new Float32Array(width * height);
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        const center = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        
        let maxDiff = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nIdx = ((y + dy) * width + (x + dx)) * 4;
            const neighbor = (data[nIdx] + data[nIdx + 1] + data[nIdx + 2]) / 3;
            maxDiff = Math.max(maxDiff, Math.abs(center - neighbor));
          }
        }
        
        edges[y * width + x] = maxDiff;
      }
    }
    
    // Find rectangular region with strong edges
    const scanStep = 20;
    let bestScore = 0;
    let bestBounds = null;
    
    for (let y1 = 0; y1 < height - 100; y1 += scanStep) {
      for (let x1 = 0; x1 < width - 100; x1 += scanStep) {
        for (let y2 = y1 + 100; y2 < height; y2 += scanStep) {
          for (let x2 = x1 + 100; x2 < width; x2 += scanStep) {
            const w = x2 - x1;
            const h = y2 - y1;
            const aspectRatio = w / h;
            
            if ((aspectRatio > 1.4 && aspectRatio < 2.2) || (aspectRatio > 0.45 && aspectRatio < 0.72)) {
              // Calculate edge strength along rectangle perimeter
              let edgeStrength = 0;
              
              // Top and bottom edges
              for (let x = x1; x < x2; x++) {
                edgeStrength += edges[y1 * width + x] + edges[y2 * width + x];
              }
              
              // Left and right edges
              for (let y = y1; y < y2; y++) {
                edgeStrength += edges[y * width + x1] + edges[y * width + x2];
              }
              
              const score = edgeStrength / (2 * (w + h));
              
              if (score > bestScore) {
                bestScore = score;
                bestBounds = {x1, y1, x2, y2};
              }
            }
          }
        }
      }
    }
    
    if (bestBounds && bestScore > 10) {
      console.log('Found high-edge region with score:', bestScore);
      return [
        { x: bestBounds.x1, y: bestBounds.y1 },
        { x: bestBounds.x2, y: bestBounds.y1 },
        { x: bestBounds.x2, y: bestBounds.y2 },
        { x: bestBounds.x1, y: bestBounds.y2 }
      ];
    }
    
    return null;
  }
  
  /**
   * Get default corner positions when detection fails
   */
  getDefaultCardCorners(width, height, options) {
    const aspectRatio = options.targetAspectRatio || 1.75;
    
    // Use a more conservative approach - assume card takes up 70% of the image
    const scaleFactor = 0.7;
    let cardWidth, cardHeight;
    
    if (width / height > aspectRatio) {
      // Image is wider than card aspect ratio
      cardHeight = height * scaleFactor;
      cardWidth = cardHeight * aspectRatio;
    } else {
      // Image is taller than card aspect ratio
      cardWidth = width * scaleFactor;
      cardHeight = cardWidth / aspectRatio;
    }
    
    // Center the card in the image
    const centerX = width / 2;
    const centerY = height / 2;
    
    const corners = [
      { x: centerX - cardWidth / 2, y: centerY - cardHeight / 2 },
      { x: centerX + cardWidth / 2, y: centerY - cardHeight / 2 },
      { x: centerX + cardWidth / 2, y: centerY + cardHeight / 2 },
      { x: centerX - cardWidth / 2, y: centerY + cardHeight / 2 }
    ];
    
    console.log('Using default centered card:', corners);
    return corners;
  }

  /**
   * Auto-detect and suggest corner positions
   */
  async suggestCorners(image) {
    // Ensure initialization
    if (!this.initialized) {
      await this.initialize();
    }
    
    try {
      console.log('Starting corner detection for image:', image.width, 'x', image.height);
      
      // Try line-based detection first (most reliable)
      try {
        const lineCorners = await lineDetector.detectCard(image);
        if (lineCorners && lineCorners.length === 4) {
          console.log('Line detection succeeded:', lineCorners);
          return lineCorners;
        }
      } catch (lineError) {
        console.warn('Line detection failed:', lineError);
      }
      
      // Try color segmentation detection
      try {
        const colorCorners = await colorDetector.detectCard(image);
        if (colorCorners && colorCorners.length === 4) {
          console.log('Color segmentation succeeded:', colorCorners);
          return colorCorners;
        }
      } catch (colorError) {
        console.warn('Color segmentation failed:', colorError);
      }
      
      // Then try with standard parameters
      let corners = await this.detectCardCorners(image, {
        minArea: 0.05,
        maxArea: 0.95,
        aspectRatioTolerance: 0.5,
        targetAspectRatio: 1.75
      });

      if (corners && corners.length === 4) {
        console.log('Successfully detected corners:', corners);
        return corners;
      }
      
      // If that fails, try manual detection with multiple thresholds
      console.log('Standard detection failed, trying manual detection with multiple thresholds');
      corners = await this.manualDetectionWithMultipleThresholds(image);
      
      if (corners && corners.length === 4) {
        console.log('Manual detection succeeded:', corners);
        return corners;
      }
      
      console.log('Using smart default corners');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = image.width;
      canvas.height = image.height;
      ctx.drawImage(image, 0, 0);
      
      return this.getSmartDefaultCorners(canvas, ctx, {
        targetAspectRatio: 1.75
      });
    } catch (error) {
      console.error('Corner detection failed:', error);
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = image.width;
      canvas.height = image.height;
      ctx.drawImage(image, 0, 0);
      
      return this.getSmartDefaultCorners(canvas, ctx, {
        targetAspectRatio: 1.75
      });
    }
  }
  
  /**
   * Try multiple threshold values to find the best card detection
   */
  async manualDetectionWithMultipleThresholds(image) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Try different threshold values
    const thresholds = [140, 160, 180, 200, 220];
    let bestCorners = null;
    let bestScore = 0;
    
    for (const threshold of thresholds) {
      console.log('Trying threshold:', threshold);
      
      // Find bright pixels with this threshold
      let minX = canvas.width, maxX = 0;
      let minY = canvas.height, maxY = 0;
      let brightPixelCount = 0;
      
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const idx = (y * canvas.width + x) * 4;
          const brightness = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
          
          if (brightness > threshold) {
            brightPixelCount++;
            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y);
          }
        }
      }
      
      // Skip if too few or too many bright pixels
      const brightPercent = brightPixelCount / (canvas.width * canvas.height);
      if (brightPercent < 0.05 || brightPercent > 0.8) {
        continue;
      }
      
      const width = maxX - minX;
      const height = maxY - minY;
      const aspectRatio = width / height;
      
      // Check if this looks like a business card
      const isLandscape = aspectRatio > 1.3 && aspectRatio < 2.3;
      const isPortrait = aspectRatio > 0.43 && aspectRatio < 0.77;
      const isSizeReasonable = width > canvas.width * 0.2 && height > canvas.height * 0.2;
      
      if ((isLandscape || isPortrait) && isSizeReasonable) {
        // Calculate score based on size and aspect ratio closeness
        const targetAspectRatio = isLandscape ? 1.75 : 0.57;
        const aspectScore = 1 - Math.abs(aspectRatio - targetAspectRatio) / targetAspectRatio;
        const sizeScore = (width * height) / (canvas.width * canvas.height);
        const score = aspectScore * 0.7 + sizeScore * 0.3;
        
        if (score > bestScore) {
          bestScore = score;
          const padding = 5;
          bestCorners = [
            { x: Math.max(0, minX - padding), y: Math.max(0, minY - padding) },
            { x: Math.min(canvas.width - 1, maxX + padding), y: Math.max(0, minY - padding) },
            { x: Math.min(canvas.width - 1, maxX + padding), y: Math.min(canvas.height - 1, maxY + padding) },
            { x: Math.max(0, minX - padding), y: Math.min(canvas.height - 1, maxY + padding) }
          ];
          console.log('Found candidate with threshold', threshold, 'score:', score.toFixed(3));
        }
      }
    }
    
    return bestCorners;
  }
}

// Export singleton instance
export const cardDetector = new CardDetector();

// Make cardDetector available globally for OpenCV initialization
if (typeof window !== 'undefined') {
  window.cardDetector = cardDetector;
}